import { h, Component, Host, State, Element, Prop, Watch, Listen, setMode, Event, EventEmitter } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { OffsetTeaserSchemeDefinition, OffsetTeaserSchemeName, ColorBars } from "./constants/offset-teaser-types";
import {
  OFFSET_TEASER_5_SCHEME_MAP,
  OFFSET_TEASER_SCHEME_CONFIG,
  OFFSET_TEASER_SCHEME_MAP
} from "./constants/offset-teaser-constants";
import { HEADLINE_TAG_H2, HEADLINE_TAG_H4 } from "../headline/constants/headline-constants";
import { getFirstNestedChild } from "../../utils/dom-helper";
import Fragment from "../../common/fragment";
import { isDesktopOrLarger, isMobile, isTablet } from "../../utils/screen-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { HeadlineTag } from "../headline/constants/headline-types";
import { useTheRightHtmlTagForHeadline, useTheRightHtmlTagForSubheadline } from "../../utils/headline-helper";
import { setCssVariable } from "../../utils/css-variables";
import {
  OFFSET_TEASER_HEADLINE_SIZE,
  OFFSET_TEASER_SUBHEADLINE_SIZE,
  OFFSET_TEASER_CONTENT_ALIGNMENT,
  OFFSET_TEASER_ALIGNMENT_HEADLINE,
  OFFSET_TEASER_ALIGNMENT_HEADLINE_MOBILE,
  OFFSET_TEASER_TWO_ALIGNMENT_HEADLINE,
  HeadlineAlignment,
  OFFSET_TEASER_FIVE_ALIGNMENT_HEADLINE,
  OFFSET_TEASER_FIVE_ALIGNMENT_HEADLINE_MOBILE,
  OFFSET_TEASER_FOUR_ALIGNMENT_HEADLINE,
  OFFSET_TEASER_FOUR_ALIGNMENT_HEADLINE_MOBILE,
  OFFSET_TEASER_THREE_ALIGNMENT_HEADLINE,
  OFFSET_TEASER_THREE_ALIGNMENT_HEADLINE_MOBILE,
  OFFSET_TEASER_TWO_ALIGNMENT_HEADLINE_MOBILE
} from "../../constants/global-constants";
import { OffsetTeaserSchemeConfiguration } from "../../constants/colors";

/**
 * @slot [unnamed] - Holds either a single eon-ui-link or an eon-ui-linklist
 * @slot image - Holds a single eon-ui-image element
 * @slot content - Holds any number of HTML elements
 */
@Component({
  tag: "eon-ui-offset-teaser",
  styleUrls: {
    default: "offset-teaser.default.scss",
    variant_two: "offset-teaser.variant_two.scss",
    variant_three: "offset-teaser.variant_three.scss",
    variant_four: "offset-teaser.variant_four.scss",
    variant_five: "offset-teaser.variant_five.scss"
  },
  assetsDirs: ["assets-offset-teaser"],
  shadow: true
})
export class OffsetTeaser implements ColorableComponent<OffsetTeaserSchemeConfiguration<OffsetTeaserSchemeDefinition>> {
  schemeConfig: OffsetTeaserSchemeConfiguration<OffsetTeaserSchemeDefinition> = OFFSET_TEASER_SCHEME_CONFIG;
  schemeMap = OFFSET_TEASER_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: OffsetTeaserSchemeDefinition;
  @State() isMobileDevice: boolean;
  @State() isTablet: boolean;
  @State() isDesktopOrLarger: boolean;
  @State() headlineTextShadow: boolean;
  @State() availableImage: HTMLEonUiImageElement;
  @State() hasImageShadow: boolean;
  @State() seoHeadlineTag: string;
  @State() seoSubheadlineTag: string;
  @State() headlineAlignment: string;
  @State() hasContent: boolean;

  /**
   * Raised when the offsetTeaser has been loaded.
   */
  @Event() offsetTeaserLoad: EventEmitter;

  /**
   * Raised when the CTA in offsetTeaser has been clicked.
   */
  @Event() offsetTeaserClick: EventEmitter;

  @Element() hostElement: HTMLEonUiOffsetTeaserElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: OffsetTeaserSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * The text of the eon-ui-headline inside the teaser (see eon-ui-headline)
   */
  @Prop() headlineText: string;

  /**
   * The text of the eon-ui-headline inside the teaser (see eon-ui-headline)
   */
  @Prop() subheadlineText: string;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline,<br>
   *   the HTML-Tag for the subheadline is set automatically depending on this property (headline-html-tag  -1 ), e.g <br>
   *    Headline is H2, then subheadline is H3, Headline is H3, then subheadline is H5, and so on
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * The imageAspectRatio sets aspect ratio for image having options 1:1 or 3:4
   */
  @Prop() imageAspectRatio: "ratio-1x1" | "ratio-3x4";

  /**
   * Color Bars option are provided:
   * Left - single narrow bar
   */
  @Prop() colorBars: ColorBars = null;

  /**
   * Color Bars position are provided:
   * Left - single narrow bar with distant margin from left
   */
  @Prop() colorBarOffset: boolean;

  /**
   * @disableControlInStorybook true
   * */
  @Prop({ reflect: true }) variant: "default" | "variant_two" | "variant_three" | "variant_four" | "variant_five" =
    "default";

  componentWillLoad() {
    this.applyScheme();
    this.handleImage();
    this.handleShadowOnImage();
    this.handleImageRatio();
    this.handleHeadlineTextShadow();
    this.handleHeadlineTag();
    this.handleHeadlineAlignment();
    this.hasContent = !!this.hostElement.querySelector('[slot="content"]');
  }

  componentDidLoad() {
    this.setCssVariables();
    this.handleColorBarsStyles();
    this.offsetTeaserLoad.emit();
  }

  @Watch("variant")
  setMode() {
    setMode(() => {
      return this.variant;
    });
  }

  @Watch("scheme")
  applyScheme() {
    if (this.variant === "variant_five") {
      this.schemeMap = OFFSET_TEASER_5_SCHEME_MAP;
    }
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.innerBackgroundColor;
  }

  @Watch("colorBars")
  handleColorBarsStyles() {
    if (!!this.colorBars) {
      setCssVariable(this.hostElement, "--currentColorBar", `var(--eon-offset-teaser-color-bar-${this.colorBars})`);
    }
  }

  /**
   * The property use-as-background is set by default to true for image component
   * If not set or editor has not set it to true in dialog
   * Then this will disfigure the design of offset teaser
   * Hence not giving flexibility to an editor to set this property via dialog
   */
  @Listen("resize", { target: "window" })
  handleShadowOnImage() {
    this.isMobileDevice = isMobile();
    this.isTablet = isTablet();
    this.isDesktopOrLarger = isDesktopOrLarger();

    if (this.availableImage) {
      this.availableImage.setAttribute("use-as-background", "true");
      switch (this.variant) {
        case "variant_two":
          break;
        case "variant_four":
          this.availableImage.setAttribute(
            "shadow-on-image",
            !this.isMobileDevice && this.hasImageShadow ? "true" : "false"
          );
          break;
        default: {
          this.availableImage.setAttribute(
            "shadow-on-image",
            this.isDesktopOrLarger && this.hasImageShadow ? "true" : "false"
          );
          break;
        }
      }
    }
  }

  @Listen("resize", { target: "window" })
  @Watch("imageAspectRatio")
  handleImageRatio() {
    if (this.availableImage) {
      /**
       * For variant_two image aspect-ratio options are: ratio-1x1 (Mobile View) | ratio-3x4
       * For variant_three image aspect-ratio options are: ratio-4x3 (Mobile View) | ratio-2x1
       */
      if (this.variant === "variant_two") {
        this.availableImage.setAttribute("aspect-ratio", this.isMobileDevice ? "ratio-1x1" : this.imageAspectRatio);
      } else if (this.variant === "variant_three") {
        this.availableImage.setAttribute("aspect-ratio", this.isMobileDevice ? "ratio-4x3" : "ratio-2x1");
      }
    }
  }

  @Listen("buttonClick")
  @Listen("linkClick")
  handleCTAOnClick(event) {
    this.offsetTeaserClick.emit(event);
  }

  private handleImage() {
    this.availableImage = getFirstNestedChild(this.hostElement, "eon-ui-image");
    this.hasImageShadow = this.availableImage ? this.availableImage.getAttribute("shadow-on-image") === "true" : false;
  }

  private handleVisibleBackgroundColor(variantColorBar) {
    const visibleColorBar = variantColorBar ? this.variant !== "variant_three" : true;
    return (
      <div
        class={pfx({
          ["visible-background"]: true,
          ["color-bar-active"]: this.colorBars && !this.isDesktopOrLarger && visibleColorBar
        })}
      ></div>
    );
  }

  private handleColorBars() {
    return (
      <Fragment>
        {(this.isDesktopOrLarger || this.variant === "variant_three" || this.variant === "variant_four") && (
          <div
            class={pfx({
              [`color-bar`]: true,
              ["color-bar-active"]: this.colorBars,
              ["offset-bar"]: this.colorBarOffset
            })}
          ></div>
        )}
      </Fragment>
    );
  }

  @Listen("resize", { target: "window" })
  handleHeadlineTextShadow() {
    // Text shadow is handled via component itself for headline on Image
    switch (this.variant) {
      case "variant_three":
        this.headlineTextShadow = this.isDesktopOrLarger ? true : false;
        break;
      case "variant_four":
        this.headlineTextShadow = this.isDesktopOrLarger || this.isTablet ? true : false;
        break;
      default:
        this.headlineTextShadow = false;
        break;
    }
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, HEADLINE_TAG_H2);
    this.seoSubheadlineTag = useTheRightHtmlTagForSubheadline(this.headlineHtmlTag, HEADLINE_TAG_H4);
  }

  @Listen("resize", { target: "window" })
  handleHeadlineAlignment() {
    switch (this.variant) {
      case "default":
        this.headlineAlignment = this.isDesktopOrLarger
          ? OFFSET_TEASER_ALIGNMENT_HEADLINE
          : OFFSET_TEASER_ALIGNMENT_HEADLINE_MOBILE;
        break;
      case "variant_two":
        this.headlineAlignment = this.isMobileDevice
          ? OFFSET_TEASER_TWO_ALIGNMENT_HEADLINE_MOBILE
          : OFFSET_TEASER_TWO_ALIGNMENT_HEADLINE;
        break;
      case "variant_three":
        this.headlineAlignment = this.isMobileDevice
          ? OFFSET_TEASER_THREE_ALIGNMENT_HEADLINE_MOBILE
          : OFFSET_TEASER_THREE_ALIGNMENT_HEADLINE;
        break;
      case "variant_four":
        this.headlineAlignment = this.isMobileDevice
          ? OFFSET_TEASER_FOUR_ALIGNMENT_HEADLINE_MOBILE
          : OFFSET_TEASER_FOUR_ALIGNMENT_HEADLINE;
        break;
      case "variant_five":
        this.headlineAlignment = this.isMobileDevice
          ? OFFSET_TEASER_FIVE_ALIGNMENT_HEADLINE_MOBILE
          : OFFSET_TEASER_FIVE_ALIGNMENT_HEADLINE;
        break;
      default:
        this.headlineAlignment = this.isDesktopOrLarger
          ? OFFSET_TEASER_ALIGNMENT_HEADLINE
          : OFFSET_TEASER_ALIGNMENT_HEADLINE_MOBILE;
        break;
    }
  }

  private headlineWrapper = () => {
    return (
      <eon-ui-headline
        class={pfx({
          "offset-teaser-headline": true,
          [`headline-alignment-${this.headlineAlignment}`]: this.headlineAlignment
        })}
        size={OFFSET_TEASER_HEADLINE_SIZE}
        headline-html-tag={this.seoHeadlineTag}
        text={this.headlineText}
        text-shadow={this.headlineTextShadow}
        alignment={this.headlineAlignment as HeadlineAlignment}
      ></eon-ui-headline>
    );
  };

  private handleHeadlineComponent() {
    // For variant_three headline on desktop or larger viewport separate headline is implemented
    switch (this.variant) {
      case "variant_three":
        if (!this.isDesktopOrLarger) return this.headlineWrapper();
        break;
      case "variant_five":
        break;
      default:
        return this.headlineWrapper();
    }
  }

  private imageWrapper = () => {
    return (
      <div
        class={pfx({
          "image-wrapper": true,
          [`${this.imageAspectRatio}`]: this.imageAspectRatio && this.variant === "variant_two",
          [`${this.isMobileDevice ? "ratio-4x3" : "ratio-2x1"}`]: this.variant === "variant_three"
        })}
      >
        {this.colorBars && this.handleColorBars()}
        <slot name="image"></slot>
      </div>
    );
  };

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "offset-teaser-wrapper": true,
            "has-color-bar": this.colorBars
          })}
        >
          {this.variant === "variant_five" && this.isMobileDevice}
          {this.variant === "variant_two" && this.handleVisibleBackgroundColor(false)}
          <div class={pfx({ "image-layer": true })}>
            {((this.isDesktopOrLarger && this.variant === "variant_three") || this.variant === "variant_five") &&
              this.headlineWrapper()}
            {this.variant !== "variant_five" && this.imageWrapper()}
          </div>
          <div class={pfx("content-layer")}>
            {this.variant !== "variant_two" && this.handleVisibleBackgroundColor(true)}
            <div
              class={pfx({
                ["content-wrapper"]: true,
                [`alignment-${OFFSET_TEASER_CONTENT_ALIGNMENT}`]: true
              })}
            >
              {/* Why 100% ??" */}
              {/* In multibrand it is possible to have different alignment between content (e.g. center) anf headline (e.g. right) */}
              {/* if "contentAlignment": "center", we need a width = 100% to override the css-rule align-items = e.g center or flx-start, flex-end  */}
              <div
                style={{ width: "100%" }}
                class={pfx({
                  [`${this.variant}`]: true
                })}
              >
                {this.handleHeadlineComponent()}
              </div>
              <eon-ui-headline
                class={pfx("offset-teaser-subheadline")}
                size={OFFSET_TEASER_SUBHEADLINE_SIZE}
                headline-html-tag={this.seoSubheadlineTag}
                text={this.subheadlineText}
                alignment={OFFSET_TEASER_CONTENT_ALIGNMENT}
              ></eon-ui-headline>
              {this.hasContent && (
                <div class={pfx("offset-teaser-content")}>
                  <slot name="content"></slot>
                </div>
              )}
              <div class={pfx("offset-teaser-links")}>
                <slot></slot>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
